"use client";

import { Suspense, useEffect, useRef } from "react";
import { getSession, signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";

type PopupMessage =
  | { type: "googlePopupAuth"; status: "success" }
  | { type: "googlePopupAuth"; status: "error"; error: string };

// Distributive Omit so each union variant keeps its own fields (e.g. `error`
// on the error variant). Plain `Omit<PopupMessage, "type">` would collapse to
// the common keys only and drop `error`.
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
type PopupPayload = DistributiveOmit<PopupMessage, "type">;

const KICKOFF_KEY = "google_popup_signin_started";

function postToOpener(payload: PopupPayload) {
  try {
    const opener = window.opener as Window | null;
    if (!opener || opener.closed) return;
    opener.postMessage({ type: "googlePopupAuth", ...payload }, window.location.origin);
  } catch {
    // Silent - worst case the parent falls back to session polling.
  }
}

/**
 * The Google OAuth popup page.
 *
 * Opened by {@link useGooglePopupAuth}. Its job is simple:
 *   - If we're already authenticated when this page loads (meaning Google
 *     already bounced us back after OAuth), close the popup. The parent
 *     window detects the new session via its own `useSession()` on focus.
 *   - Otherwise, trigger `signIn('google')` exactly once, with a callbackUrl
 *     that points back to THIS page so the roundtrip lands us here again
 *     and we can self-close on the next render.
 *
 * The sessionStorage kickoff guard survives React 18 Strict Mode's double
 * effect invocation in dev. Two concurrent `signIn()` calls would race -
 * the second call clobbers the first and NextAuth ends up redirecting the
 * popup to the fallback `/auth/signin` page with a generic error instead
 * of forwarding to Google.
 */
function GooglePopupContent(): null {
  const searchParams = useSearchParams();
  const parentCallbackUrl = searchParams.get("callbackUrl") || "/";
  const popupError = searchParams.get("error");
  const hasClosedRef = useRef(false);

  useEffect(() => {
    if (hasClosedRef.current) return;

    if (popupError) {
      hasClosedRef.current = true;
      sessionStorage.removeItem(KICKOFF_KEY);
      postToOpener({ status: "error", error: popupError });
      window.setTimeout(() => window.close(), 50);
      return;
    }

    let cancelled = false;

    void (async () => {
      const session = await getSession();
      if (cancelled || hasClosedRef.current) return;

      if (session?.user) {
        hasClosedRef.current = true;
        sessionStorage.removeItem(KICKOFF_KEY);
        postToOpener({ status: "success" });
        window.setTimeout(() => window.close(), 50);
        return;
      }

      if (sessionStorage.getItem(KICKOFF_KEY)) return;
      sessionStorage.setItem(KICKOFF_KEY, "1");

      const popupCallbackUrl = `/auth/popup/google?callbackUrl=${encodeURIComponent(parentCallbackUrl)}`;

      await signIn("google", {
        callbackUrl: popupCallbackUrl,
        redirect: true,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [parentCallbackUrl, popupError]);

  return null;
}

export default function GooglePopupPage() {
  return (
    <Suspense fallback={null}>
      <GooglePopupContent />
    </Suspense>
  );
}
